// react
import * as React from 'react';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { useSnackbar } from 'notistack';

import { FormikProvider, useFormik, Form } from 'formik';

// IconButton
import { Icon } from '@iconify/react';
import CloseIcon from '@iconify/icons-mdi/close-circle-outline';
import commentOutline from '@iconify/icons-mdi/comment-outline';
import commentText from '@iconify/icons-mdi/comment-text';
import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  ListItemText
} from '@mui/material';

// local
import { editItem } from '../../fetch';
import { reportTripsActions } from '../../store';
import updateFlightList from '../flightList/updateFlightList';
import { useTranslation } from '../../common/LocalizationProvider';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <Icon icon={CloseIcon} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

FlightReviewComment.propTypes = {
  reportTrip: PropTypes.object.isRequired,
  setIsOpenSpeedDial: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default function FlightReviewComment({ reportTrip, setIsOpenSpeedDial, disabled }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const ref = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: reportTrip.favoriteAffectationVols,
      remarquableTrace: reportTrip.pointRemarquableTrace > 0
    },
    onSubmit: async (values) => {
      if (!disabled) {
        const response = await editItem('affectationVols', values.id, values);
        if (response.ok) {
          enqueueSnackbar(t('FL_Menu_Comment_Success'), {
            variant: 'success'
          });
          dispatch(reportTripsActions.update(await updateFlightList(values)));
        } else {
          const error = await response.text();
          const message = error.slice(0, error.indexOf('-'));
          enqueueSnackbar(message, { variant: 'error' });
        }
      }
      setSubmitting(false);
      setIsOpen(false);
      setIsOpenSpeedDial(false);
    }
  });
  const { handleSubmit, isSubmitting, getFieldProps, setSubmitting } = formik;

  return (
    <>
      <MenuItem
        sx={{ color: 'text.secondary' }}
        onClick={() => setIsOpen(true)}
        ref={ref}
        disabled={disabled}
      >
        <Icon
          icon={
            reportTrip.commentAffectationVols !== null &&
            reportTrip.commentAffectationVols !== '' &&
            reportTrip.commentAffectationVols !== ' ' &&
            reportTrip.commentAffectationVols !== '0'
              ? commentText
              : commentOutline
          }
          width={23}
          height={23}
        />
        <ListItemText
          primary={t('FR_Tooltip_AddComment')}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </MenuItem>

      <BootstrapDialog onClose={() => setIsOpen(false)} open={isOpen}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <BootstrapDialogTitle onClose={() => setIsOpen(false)}>
              {t('FL_Menu_Comment')}
            </BootstrapDialogTitle>
            <DialogContent dividers>
              <TextField
                fullWidth
                sx={{ width: '400px' }}
                multiline
                rows={4}
                variant="standard"
                {...getFieldProps('comment')}
                value={getFieldProps('comment').value || ''}
              />
            </DialogContent>
            <DialogActions>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={<Icon icon={saveIcon} />}
              >
                {t('btn_Save')}
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </BootstrapDialog>
    </>
  );
}
